import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AlertCircle, ArrowRight, LoaderCircle, Plus, Save, Users, Wand2 } from "lucide-react";
import {
  createSegment,
  updateSegment,
  useSegment,
  useSegments,
  type Segment,
  type SegmentCondition,
  type SegmentConditionOperator,
  type SegmentFilterGroup,
  type SegmentOperator,
} from "@/hooks/use-segments";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/skeleton";
import { formatDate, formatNumber } from "@/lib/i18n";

type FilterFieldType = "number" | "text" | "date" | "select" | "tag";

interface FilterFieldDefinition {
  key: string;
  label: string;
  description: string;
  supportedOnContacts: boolean;
  type: FilterFieldType;
  options?: string[];
  operators: SegmentConditionOperator[];
}

const FILTER_FIELDS: FilterFieldDefinition[] = [
  {
    key: "score",
    label: "Lead score",
    description: "Behavioral score from mounted CRM intelligence.",
    supportedOnContacts: true,
    type: "number",
    operators: ["gt", "gte", "lt", "lte", "eq", "between"],
  },
  {
    key: "lifecycleStage",
    label: "Lifecycle stage",
    description: "Lead, MQL, SQL, customer, or churned.",
    supportedOnContacts: true,
    type: "select",
    options: ["lead", "mql", "sql", "customer", "churned"],
    operators: ["eq", "neq", "in", "not_in"],
  },
  {
    key: "tag",
    label: "Tag",
    description: "Any contact tag stored on the profile.",
    supportedOnContacts: true,
    type: "tag",
    operators: ["contains", "not_contains", "eq", "in"],
  },
  {
    key: "source",
    label: "Source",
    description: "Mounted acquisition source on the contact record.",
    supportedOnContacts: true,
    type: "text",
    operators: ["eq", "neq", "contains", "starts_with"],
  },
  {
    key: "createdAt",
    label: "Created date",
    description: "Date the contact first entered Stero.",
    supportedOnContacts: true,
    type: "date",
    operators: ["gte", "lte", "between"],
  },
  {
    key: "custom.city",
    label: "City",
    description: "City stored as a custom field.",
    supportedOnContacts: true,
    type: "text",
    operators: ["eq", "contains", "starts_with"],
  },
  {
    key: "email",
    label: "Email domain",
    description: "Partial or full email address matching.",
    supportedOnContacts: false,
    type: "text",
    operators: ["contains", "ends_with", "eq"],
  },
  {
    key: "custom.company",
    label: "Custom field: company",
    description: "Own-data company field captured on the contact profile.",
    supportedOnContacts: true,
    type: "text",
    operators: ["eq", "contains", "starts_with"],
  },
];

const DEFAULT_FILTER_FIELD = FILTER_FIELDS[0] as FilterFieldDefinition;

const DEFAULT_SEGMENT_TYPE = "dynamic" as const;
const EMPTY_GROUP = (): SegmentFilterGroup => ({
  operator: "AND",
  conditions: [
    {
      field: "score",
      op: "gte",
      value: 50,
    },
  ],
});

export default function SegmentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const segmentId = searchParams.get("segment");

  const { segments, isLoading, error, refetch } = useSegments();
  const { segment: activeSegment, isLoading: isSegmentLoading, error: segmentError } = useSegment(segmentId);

  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftType, setDraftType] = useState<"dynamic" | "static">(DEFAULT_SEGMENT_TYPE);
  const [draftFilterTree, setDraftFilterTree] = useState<SegmentFilterGroup>(EMPTY_GROUP);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const selectedSegment = useMemo(
    () => segments.find((segment) => segment.id === segmentId) ?? activeSegment ?? null,
    [activeSegment, segmentId, segments],
  );

  useEffect(() => {
    if (!selectedSegment) {
      setDraftName("");
      setDraftDescription("");
      setDraftType(DEFAULT_SEGMENT_TYPE);
      setDraftFilterTree(EMPTY_GROUP());
      return;
    }

    setDraftName(selectedSegment.name);
    setDraftDescription(selectedSegment.description ?? "");
    setDraftType(selectedSegment.type);
    setDraftFilterTree(selectedSegment.filterTree ?? EMPTY_GROUP());
  }, [selectedSegment]);

  const supportedFields = FILTER_FIELDS.filter((field) => field.supportedOnContacts).length;
  const unsupportedFields = useMemo(() => collectUnsupportedFields(draftFilterTree), [draftFilterTree]);

  const handleCreateNew = () => {
    setSearchParams(new URLSearchParams());
    setFormError(null);
    setFormNotice(null);
  };

  const handleSave = async () => {
    const trimmedName = draftName.trim();
    if (!trimmedName) {
      setFormError("Name the segment before saving it.");
      return;
    }

    setIsSaving(true);
    setFormError(null);
    setFormNotice(null);

    try {
      const payload = {
        name: trimmedName,
        description: draftDescription.trim() || undefined,
        type: draftType,
        filterTree: draftType === "dynamic" ? draftFilterTree : undefined,
      };

      const saved = selectedSegment
        ? await updateSegment(selectedSegment.id, {
            ...payload,
            description: draftDescription.trim() || null,
            filterTree: draftType === "dynamic" ? draftFilterTree : null,
          })
        : await createSegment(payload);

      setFormNotice(selectedSegment ? "Segment updated on the mounted CRM contract." : "Segment created on the mounted CRM contract.");
      await refetch();
      setSearchParams(new URLSearchParams({ segment: saved.id }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to save segment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewContacts = () => {
    if (!selectedSegment) {
      return;
    }
    navigate(`/crm/contacts?segment=${selectedSegment.id}`);
  };

  return (
    <div className="density-gap space-y-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Smart Segments</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Build behavioral and demographic audiences on the mounted CRM segment contract, then hand the supported rules straight into the contacts workspace.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)]/80 px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
            {supportedFields} contact-list fields supported
          </div>
          <button
            type="button"
            onClick={handleCreateNew}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
          >
            <Plus className="h-4 w-4" />
            New segment
          </button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[22rem,minmax(0,1fr)]">
        <aside className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                  Segment library
                </p>
                <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
                  Saved audiences
                </h2>
              </div>
              <div className="rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
                {segments.length}
              </div>
            </div>

            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              Pick an existing audience to refine it, or start a new one with supported demographic and behavioral rules.
            </p>

            {error && (
              <Banner tone="error">{error}</Banner>
            )}

            {isLoading ? (
              <div className="mt-4 space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 rounded-xl" />
                ))}
              </div>
            ) : segments.length === 0 ? (
              <div className="mt-4">
                <EmptyState
                  icon={<Users className="h-8 w-8" />}
                  title="No segments yet"
                  description="Create your first smart segment to reuse a live CRM audience."
                  actionLabel="Create segment"
                  onAction={handleCreateNew}
                />
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {segments.map((segment) => {
                  const isActive = segment.id === selectedSegment?.id;
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      onClick={() => setSearchParams(new URLSearchParams({ segment: segment.id }))}
                      className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                        isActive
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8"
                          : "border-[var(--color-border)] bg-background/80 hover:bg-[var(--color-surface)]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-[var(--color-foreground)]">{segment.name}</p>
                        <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                          {segment.type}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {segment.description || "No description yet."}
                      </p>
                      <div className="mt-3 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                        <span>{segment.cachedCount != null ? `${formatNumber(segment.cachedCount)} contacts` : "Count pending"}</span>
                        <span>{formatDate(segment.updatedAt)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </aside>

        <section className="space-y-4">
          {segmentError && <Banner tone="error">{segmentError}</Banner>}
          {formError && <Banner tone="error">{formError}</Banner>}
          {formNotice && <Banner tone="success">{formNotice}</Banner>}

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                  Segment builder
                </p>
                <h2 className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">
                  {selectedSegment ? "Refine live segment" : "Create live segment"}
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
                  Dynamic segments evaluate rules continuously. Static lists still save on the same mounted CRM contract, but do not need rule logic.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {selectedSegment && (
                  <button
                    type="button"
                    onClick={handleViewContacts}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-surface)]"
                  >
                    <Users className="h-4 w-4" />
                    View contacts
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || isSegmentLoading}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {selectedSegment ? "Save segment" : "Create segment"}
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr),19rem]">
              <div className="space-y-4">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Name</span>
                  <input
                    type="text"
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                    placeholder="High-intent referrals"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Description</span>
                  <textarea
                    value={draftDescription}
                    onChange={(event) => setDraftDescription(event.target.value)}
                    placeholder="What should this audience capture?"
                    rows={3}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(["dynamic", "static"] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setDraftType(type)}
                      className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                        draftType === type
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8"
                          : "border-[var(--color-border)] bg-background/70 hover:bg-[var(--color-surface)]"
                      }`}
                    >
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{type === "dynamic" ? "Dynamic segment" : "Static list"}</p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {type === "dynamic"
                          ? "Continuously evaluates live CRM rules."
                          : "Saved audience shell without rule evaluation."}
                      </p>
                    </button>
                  ))}
                </div>

                {draftType === "dynamic" ? (
                  <SegmentGroupEditor group={draftFilterTree} onChange={setDraftFilterTree} depth={0} />
                ) : (
                  <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-background/70 p-4 text-sm text-[var(--color-muted-foreground)]">
                    Static lists stay saved on the same CRM segment seam. Switch back to dynamic if you want live AND/OR rules.
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-[var(--color-border)] bg-background/80 p-4">
                  <div className="inline-flex items-center gap-2 rounded-full border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-3 py-1 text-xs font-medium text-[var(--color-ai-foreground)]">
                    <Wand2 className="h-3.5 w-3.5" />
                    Contacts handoff reality check
                  </div>
                  <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
                    The contacts page can already apply live rules for score, lifecycle stage, tag, source, and created-date boundaries. Everything else stays explicit instead of silently pretending it worked.
                  </p>

                  {unsupportedFields.length > 0 ? (
                    <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/6 p-3 text-sm text-amber-700 dark:text-amber-300">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Some rules will stay informational on the contacts list.</p>
                          <p className="mt-1 text-xs">
                            Unsupported fields: {unsupportedFields.join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/6 p-3 text-sm text-emerald-700 dark:text-emerald-300">
                      All current rules map cleanly onto the mounted contacts filter surface.
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-[var(--color-border)] bg-background/80 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                    Available field types
                  </p>
                  <div className="mt-3 space-y-2">
                    {FILTER_FIELDS.map((field) => (
                      <div key={field.key} className="rounded-xl border border-[var(--color-border)] px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[var(--color-foreground)]">{field.label}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            field.supportedOnContacts
                              ? "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300"
                              : "bg-amber-500/12 text-amber-700 dark:text-amber-300"
                          }`}>
                            {field.supportedOnContacts ? "contacts-ready" : "builder-only"}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{field.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-[var(--color-border)] bg-background/80 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                    Downstream surfaces
                  </p>
                  <div className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
                    <div className="flex items-center justify-between gap-2 rounded-xl border border-[var(--color-border)] px-3 py-2">
                      <span>Email campaigns and messaging already read `/crm/segments`.</span>
                      <Link to="/email/lists" className="inline-flex items-center gap-1 text-[var(--color-primary)]">
                        Email lists <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-xl border border-[var(--color-border)] px-3 py-2">
                      <span>Contacts handoff keeps unsupported rules visible instead of hiding them.</span>
                      <button
                        type="button"
                        onClick={handleViewContacts}
                        disabled={!selectedSegment}
                        className="inline-flex items-center gap-1 text-[var(--color-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-muted-foreground)]"
                      >
                        View contacts <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function SegmentGroupEditor({
  group,
  onChange,
  depth,
}: {
  group: SegmentFilterGroup;
  onChange: (group: SegmentFilterGroup) => void;
  depth: number;
}) {
  return (
    <div className={`space-y-3 rounded-2xl border p-4 ${depth === 0 ? "border-[var(--color-border)] bg-background/75" : "border-dashed border-[var(--color-border)] bg-[var(--color-surface)]/60"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">
            {depth === 0 ? "Root rule group" : `Nested group ${depth}`}
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Combine conditions with AND or OR.
          </p>
        </div>
        <select
          aria-label={`Group operator ${depth}`}
          value={group.operator}
          onChange={(event) => onChange({ ...group, operator: event.target.value as SegmentOperator })}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>

      <div className="space-y-3">
        {group.conditions.map((condition, index) => (
          "operator" in condition ? (
            <div key={`group-${depth}-${index}`} className="space-y-2 rounded-xl border border-dashed border-[var(--color-border)] p-3">
              <SegmentGroupEditor
                group={condition}
                depth={depth + 1}
                onChange={(nextGroup) => {
                  const nextConditions = [...group.conditions];
                  nextConditions[index] = nextGroup;
                  onChange({ ...group, conditions: nextConditions });
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const nextConditions = group.conditions.filter((_, currentIndex) => currentIndex !== index);
                  onChange({
                    ...group,
                    conditions: nextConditions.length > 0 ? nextConditions : [{ field: "score", op: "gte", value: 50 }],
                  });
                }}
                className="rounded-md border border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
              >
                Remove nested group
              </button>
            </div>
          ) : (
            <SegmentConditionEditor
              key={`condition-${depth}-${index}`}
              condition={condition}
              onChange={(nextCondition) => {
                const nextConditions = [...group.conditions];
                nextConditions[index] = nextCondition;
                onChange({ ...group, conditions: nextConditions });
              }}
              onRemove={() => {
                const nextConditions = group.conditions.filter((_, currentIndex) => currentIndex !== index);
                onChange({
                  ...group,
                  conditions: nextConditions.length > 0 ? nextConditions : [{ field: "score", op: "gte", value: 50 }],
                });
              }}
            />
          )
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange({
            ...group,
            conditions: [...group.conditions, { field: "source", op: "eq", value: "" }],
          })}
          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
        >
          + Rule
        </button>
        <button
          type="button"
          onClick={() => onChange({
            ...group,
            conditions: [...group.conditions, EMPTY_GROUP()],
          })}
          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
        >
          + Nested group
        </button>
      </div>
    </div>
  );
}

function SegmentConditionEditor({
  condition,
  onChange,
  onRemove,
}: {
  condition: SegmentCondition;
  onChange: (condition: SegmentCondition) => void;
  onRemove: () => void;
}) {
  const fieldDefinition = FILTER_FIELDS.find((field) => field.key === condition.field) ?? DEFAULT_FILTER_FIELD;

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-background/85 p-3">
      <div className="grid gap-2 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.9fr),minmax(0,1fr),auto]">
        <label className="space-y-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Field</span>
          <select
            aria-label="Segment field"
            value={fieldDefinition.key}
            onChange={(event) => {
              const nextField = FILTER_FIELDS.find((field) => field.key === event.target.value) ?? DEFAULT_FILTER_FIELD;
              const nextOperator = nextField.operators[0] ?? "eq";
              onChange({
                field: nextField.key,
                op: nextOperator,
                value: defaultValueForField(nextField),
              });
            }}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          >
            {FILTER_FIELDS.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Operator</span>
          <select
            aria-label="Segment operator"
            value={condition.op}
            onChange={(event) => onChange({ ...condition, op: event.target.value as SegmentConditionOperator })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          >
            {fieldDefinition.operators.map((operator) => (
              <option key={operator} value={operator}>
                {humanizeOperator(operator)}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Value</span>
          <ConditionValueInput field={fieldDefinition} condition={condition} onChange={onChange} />
        </label>

        <div className="flex items-end">
          <button
            type="button"
            onClick={onRemove}
            className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
          >
            Remove
          </button>
        </div>
      </div>

      <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
        {fieldDefinition.description}
      </p>
    </div>
  );
}

function ConditionValueInput({
  field,
  condition,
  onChange,
}: {
  field: FilterFieldDefinition;
  condition: SegmentCondition;
  onChange: (condition: SegmentCondition) => void;
}) {
  if (condition.op === "exists" || condition.op === "not_exists") {
    return (
      <div className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
        No value needed
      </div>
    );
  }

  if (condition.op === "between") {
    const [start, end] = Array.isArray(condition.value) ? condition.value : ["", ""];
    return (
      <div className="grid grid-cols-2 gap-2">
        <input
          type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
          value={typeof start === "string" || typeof start === "number" ? String(start) : ""}
          onChange={(event) => onChange({ ...condition, value: [event.target.value, typeof end === "string" || typeof end === "number" ? end : ""] })}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
        <input
          type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
          value={typeof end === "string" || typeof end === "number" ? String(end) : ""}
          onChange={(event) => onChange({ ...condition, value: [typeof start === "string" || typeof start === "number" ? start : "", event.target.value] })}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      </div>
    );
  }

  if (field.type === "select" && field.options) {
    return (
      <select
        aria-label="Segment value"
        value={typeof condition.value === "string" ? condition.value : ""}
        onChange={(event) => onChange({ ...condition, value: event.target.value })}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
      >
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      aria-label="Segment value"
      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
      value={typeof condition.value === "string" || typeof condition.value === "number" ? String(condition.value) : ""}
      onChange={(event) => onChange({
        ...condition,
        value: field.type === "number" ? Number(event.target.value) : event.target.value,
      })}
      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
    />
  );
}

function defaultValueForField(field: FilterFieldDefinition): unknown {
  if (field.type === "number") {
    return 0;
  }
  if (field.type === "select" && field.options && field.options.length > 0) {
    return field.options[0];
  }
  if (field.operators.includes("between")) {
    return ["", ""];
  }
  return "";
}

function collectUnsupportedFields(group: SegmentFilterGroup): string[] {
  const unsupported = new Set<string>();

  const walk = (node: SegmentFilterGroup) => {
    for (const condition of node.conditions) {
      if ("operator" in condition) {
        walk(condition);
        continue;
      }

      const field = FILTER_FIELDS.find((entry) => entry.key === condition.field);
      if (field && !field.supportedOnContacts) {
        unsupported.add(field.label);
      }
    }
  };

  walk(group);
  return Array.from(unsupported);
}

function humanizeOperator(operator: SegmentConditionOperator) {
  return operator.replaceAll("_", " ");
}

function Banner({ children, tone }: { children: string; tone: "error" | "success" }) {
  const toneClasses = tone === "error"
    ? "border-destructive/20 bg-destructive/5 text-destructive"
    : "border-emerald-500/20 bg-emerald-500/6 text-emerald-700 dark:text-emerald-300";

  return (
    <div role={tone === "error" ? "alert" : "status"} className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${toneClasses}`}>
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
